import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import { fetchProjectIntelligence, type ProjectIntelligenceResponse } from '~/api/projectsV3';
import { useIntelligenceTabRedesignEnabled } from './useIntelligenceTabRedesignEnabled';

export interface UseProjectIntelligenceOptions {
  projectId?: string;
}

export function projectIntelligenceQueryKey(projectId: string | undefined) {
  return ['projectIntelligence', { projectId }] as const;
}

export function projectIntelligenceQueryEnabled(projectId: string | undefined, redesignEnabled: boolean): boolean {
  return !!projectId && redesignEnabled;
}

export function useProjectIntelligence(
  { projectId }: UseProjectIntelligenceOptions,
  options?: Partial<UseQueryOptions<ProjectIntelligenceResponse, Error, ProjectIntelligenceResponse>>,
) {
  const { resolved, enabled } = useIntelligenceTabRedesignEnabled();

  const redesignEnabled = resolved && enabled;

  return useQuery({
    enabled: projectIntelligenceQueryEnabled(projectId, redesignEnabled),
    queryKey: projectIntelligenceQueryKey(projectId),
    staleTime: 1000 * 30, // 30 seconds
    queryFn: ({ signal }) => {
      if (!projectId) {
        throw new Error('projectId is required');
      }
      return fetchProjectIntelligence(projectId, { signal });
    },
    ...options,
  });
}
